
import React from 'react';

export interface PromotedDish {
  id?: string; // ID esistente o 'temporary'
  name: string;
  description: string;
  price: string;
  originalPrice?: string;
  imageUrl?: string;
  badgeType: 'limited' | 'today' | 'chef';
  isActive: boolean;
  // Variants — same model as MenuItem, for parity with the menu editor. Optional (legacy promoted
  // dishes have none). Each variant has a label + price. NOTE: no priceCents stored here — the `price`
  // string is the single source of truth and getEffectivePriceCents() derives cents on read, avoiding
  // the stale-cents footgun (same rule applied to menu items).
  variants?: {
    id: string;
    label: string;
    price: string;
  }[];
  hideBasePrice?: boolean;
  basePriceLabel?: string;
  translations?: {
    [key: string]: {
      name: string;
      description: string;
      // Per-language variant labels (incl. the __base__ pseudo-variant for basePriceLabel), mirrors MenuItem.
      variantLabels?: { [variantId: string]: string };
    }
  };
  allergenIds?: string[]; // Allergeni del piatto
  tagIds?: string[]; // Tag del piatto
}

// Customer Preferences Interface (Centralized)
export interface CustomerPreferences {
  marketingAIHtml?: string;
  calendarEnabled?: boolean;
  calendarOnlyWhenClosed?: boolean;
  colorThemePreset?: string;
  customBgColor?: string;
  customTitleColor?: string;
  customCategoryColor?: string;
  customTextColor?: string;
  customDescriptionColor?: string;
  customAccentColor?: string;
  pageLayoutPreset?: string;
  specialClosures?: string;
  whatsappShowOnProfile?: boolean;
  whatsappShowOnlyWhenClosed?: boolean;
  marketingLastGenerationDate?: string;
  emailShowOnProfile?: boolean;
  promotedDishJson?: string;
  notificationWhatsAppType?: 'none' | 'locale' | 'owner' | 'custom';
  notificationWhatsAppCustomNumber?: PhoneNumber;
  // Email notification settings (NEW)
  notificationEmailType?: 'none' | 'public' | 'owner' | 'custom';
  notificationEmailCustomAddress?: string;
  fullyBookedDates?: string[]; // [NEW] Dates where booking is stopped (not closed)
  hideTotalInReceipt?: boolean;
  hideMyOrder?: boolean; // Nasconde tutti gli elementi del "Mio Ordine" nel menu pubblico (pulsante floating + pulsanti "+")
  bookingPhoneShowOnProfile?: boolean;
  priceZeroCheckEnabled?: boolean; // Verifica automatica piatti senza prezzo all'apertura/modifica. Default ON (undefined = attivo).
                                   // Off per chi tiene VOLUTAMENTE piatti senza prezzo (es. "Chiedere al personale") ed evita il modal ricorrente.
  priceShowDecimals?: boolean; // Mostra i decimali sui prezzi (12,00 € vs 12 €). Default ON (undefined = mostrati).
  // Additional languages settings (NEW)
  multilingualEnabled?: boolean; // Master switch for the menu's multilingual system. Default ON (undefined = on).
                                 // OFF = the menu is single-language (sourceLanguage). Hides translation actions,
                                 // badges and the public language switcher; the completion tracker drops the
                                 // translation points. Does NOT delete existing translations (reversible).
  additionalLanguages?: string[]; // Additional languages beyond base 5 (max 4 additional)
  defaultLanguage?: string; // Default language for public menu (fallback)
  sourceLanguage?: string; // Language the menu was authored/imported in (AI-detected). The base fields
                           // (item.name, variant.label, basePriceLabel, categoryName) are in this language.
                           // Distinct from defaultLanguage (app/owner preference). Defaults to 'it'.
  showMenuButtonOnProfile?: boolean; // Mostra/nasconde il pulsante "Scopri il menu" nel profilo pubblico. Default true (undefined = mostrato).
  customButtonShowOnProfile?: boolean; // Mostra il pulsante personalizzato nel profilo pubblico. Default FALSE (undefined = nascosto) — a differenza
                                        // degli altri toggle contatti, qui non c'è alcun valore preesistente per nessun ristorante.
}

export interface PhoneNumber {
  prefix: string;
  number: string;
}

export interface BusinessInfo {
  name: string;
  subtitle?: string;
  address: string;
  city: string;
  phone: string;
  province: string;
  zipCode: string;
  internalPhone: PhoneNumber;
  bookingPhone: PhoneNumber;
  whatsappPhone: PhoneNumber;
  restaurantEmail?: string;
  showWhatsappButton: boolean;
  hideWhatsappWhenOpen?: boolean;
  reviewUrl?: string;
  websiteUrl?: string;
  country?: string;
  customButtonLabel?: string; // Nome del pulsante personalizzato nel profilo pubblico (lingua source)
  customButtonUrl?: string; // URL esterno aperto dal pulsante personalizzato
  customButtonLabelTranslations?: { [lang: string]: string }; // Traduzioni di customButtonLabel per lingua, stesso pattern di ProfileInfo.bioTranslations
}

export interface SocialLink {
  id: string;
  platform: string;
  url: string;
}

export interface DayOpeningHours {
  day: 'Lunedì' | 'Martedì' | 'Mercoledì' | 'Giovedì' | 'Venerdì' | 'Sabato' | 'Domenica';
  isOpen: boolean;
  slots: { from: string; to: string }[];
}

export interface TypographySettings {
  family?: string;
  size?: string; // e.g. '18px', '1.2rem'
  weight?: string; // e.g. '400', '700', 'bold'
}

export interface ProfileInfo {
  logoUrl: string;
  coverUrl: string;
  bio: string;
  bioTranslations?: { [key: string]: string };
  openingHours: DayOpeningHours[];
  holidayClosures?: string[]; // YYYY-MM-DD
  socialLinks: SocialLink[];
  theme: 'light' | 'dark' | 'warm' | 'fresh' | 'custom';
  layout: 'classic' | 'grid' | 'image-centric' | 'elegant' | 'minimalist' | 'cafe' | 'tropical' | 'vintage' | 'ocean' | 'prisma';

  // Font Settings
  fontPreset: 'default' | 'custom' | 'inter' | 'playfair' | 'lato' | 'montserrat' | 'oswald' | 'raleway' | 'roboto' | 'dancing' | 'amatic' | 'fredoka' | 'pinyon' | 'chewy';
  customFonts?: {
    primary: string; // Global fallback
    restaurantName?: TypographySettings;
    categoryName?: TypographySettings;
    dishName?: TypographySettings;
    bodyText?: TypographySettings;
  };
  customThemeColors?: {
    background: string;
    text: string;
    accent: string;
  };
  gallery?: string[]; // [NEW] Public Gallery Images
  services?: string[]; // IDs of selected services
  timezone?: string; // IANA timezone (e.g. "Europe/Rome", "Atlantic/Canary") — single source of truth
}

export interface Allergen {
  id: string;
  name: string;
  translations?: {
    [key: string]: {
      name: string;
    }
  };
}

/**
 * Reserved variant id used to make the item's "base price" row label (basePriceLabel)
 * translatable through the existing variantLabels infrastructure. It is NOT a real variant:
 * it never appears in MenuItem.variants, only as a key inside translations[lang].variantLabels.
 * Reading: translations[lang]?.variantLabels?.[BASE_VARIANT_LABEL_ID] ?? basePriceLabel ?? t('menu.standard_price').
 */
export const BASE_VARIANT_LABEL_ID = '__base__';

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: string;
  priceCents?: number | null;
  variants?: {
    id: string;
    label: string;
    price: string;
    priceCents?: number | null;
  }[];
  allergenIds: string[];
  tagIds: string[];
  isActive: boolean;
  temporary_reopen_at?: string | null; // Data riapertura automatica (ISO string)
  hideBasePrice?: boolean;
  basePriceLabel?: string;
  imageUrl?: string;
  translations?: {
    [key: string]: {
      name: string;
      description: string;
      variantLabels?: { [variantId: string]: string };
      _srcHash?: string; // Reserved: source signature at translation time (see translationStatus.ts).
                         // Verified safe vs DB constraint validate_menu_item_translations: it only
                         // requires name+description as strings, extra keys are allowed.
    }
  };
}

export interface MenuCategory {
  id: string;
  categoryName: string;
  description?: string;
  imageUrl?: string;
  items: MenuItem[];
  sort?: number;
  translations?: {
    [key: string]: {
      categoryName: string;
      description?: string;
      /** Source hash stamped after AI translation — same semantics as MenuItem._srcHash. */
      _srcHash?: string;
    }
  };
}

export interface User {
  id?: string | number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string; // Telefono personale (non pubblico)
  login_token?: string;
  lang?: string;
}

export interface BillingInfo {
  paese?: 'ES' | 'IT' | 'FR' | 'DE' | 'PT' | 'AT' | 'BE' | 'NL' | 'OTHER';
  ragione_sociale: string;
  piva: string;
  cf: string;
  pec: string;
  sdi: string;
  // Indirizzo completo
  indirizzo: string; // Via e numero civico
  citta: string; // Città/Comune
  provincia: string; // Provincia (sigla o nome completo)
  cap: string; // Codice Avviamento Postale
  stato: string; // Stato/Paese (auto-popolato dal campo 'paese')
  comune: string; // Deprecato, mantenuto per backward compatibility
}

export type BookingStatus = 'disabled' | 'enabled' | 'paused' | 'reset';

export interface BookingSettings {
  enabled: boolean;
  bookingStatus?: BookingStatus; // Stato macchina a stati: disabled | enabled | paused | reset
  hideBookingWhenOpen?: boolean; // Se true, nasconde il widget quando il ristorante è aperto (in base agli orari)
  duration: number; // Durata media prenotazione in minuti
  minNotice: number; // Preavviso minimo in minuti
  maxAdvance: number; // Anticipo massimo in giorni
  cutoffTime: string; // Orario limite prenotazione (HH:mm)
  publicOpeningTime?: string; // Orario di apertura pubblico per sincronizzare l'inizio degli orari prenotabili (HH:mm)
  bookingMode: 'infinite' | 'table_based'; // Modalità di prenotazione
  cancellationPolicyHours?: number; // Ore entro cui è possibile cancellare
  // New fields for enhanced notification system
  booking_prefix?: string; // Prefix for booking numbers (e.g., "DSK")
  booking_seq?: number; // Current sequence number
  notification_language?: 'it' | 'en' | 'es' | 'fr' | 'de'; // Restaurant notification language
  notification_preferences?: {
    whatsapp: boolean;
    email: boolean;
    custom_email?: string | null;
    custom_phone?: string | null;
  };
  enable_cancellation?: boolean; // Allow customer cancellation
  autoConfirm?: boolean; // New: Auto-confirm online bookings
  timezone?: string; // Restaurant timezone (e.g., "Europe/Rome")
  iframeUrl?: string; // Embedded iframe URL for booking widget

  // NUOVO: Limite posti per modalità infinite
  infiniteModeMaxSeats?: number; // Default 20

  // NUOVO: Reminder automatici prenotazioni
  enable_booking_reminders?: boolean; // Default true - invia reminder 3h prima se prenotazione >6h

  // G2: Tavolata Massima — Numero massimo ospiti per tavolo
  maxPartySize?: number | null;    // Limite massimo di persone accettate in una singola prenotazione
  acceptLargeGroups?: boolean;     // Deprecated — rimosso dalla UI, mantenuto per retrocompatibilità DB

  // NUOVO: Modalità slot e configurazione manuale
  slotMode?: 'auto' | 'manual'; // Default 'auto'
  manualSlots?: {
    mon?: string[];
    tue?: string[];
    wed?: string[];
    thu?: string[];
    fri?: string[];
    sat?: string[];
    sun?: string[];
  };
}

export interface Table {
  id: string;
  name: string;
  capacity: number;
  seats?: number;          // alias DB per capacity — usato in checkAvailability
  roomId?: string;
  sort_order?: number;
  deletedAt?: string | null; // G: soft delete timestamp. null = attivo.
}

export interface Room {
  id: string;
  name: string;
  tables: Table[];
}

export interface Booking {
  id: string;
  restaurantId?: string;
  restaurantName?: string; // Solo admin cross-restaurant (BookingDeepSearch): join restaurants(name)
  updatedAt?: string; // Solo admin (BookingDeepSearch): DB updated_at, proxy di "quando" per stato/cancellazione
  booking_date: string; // YYYY-MM-DD
  booking_time: string; // HH:mm
  partySize: number;
  name: string;
  phone: string;
  email: string;
  notes?: string;
  createdAt: string; // ISO string
  tableId?: string;
  roomId?: string; // Optional helper
  status: 'confirmed' | 'pending' | 'cancelled' | 'no_show'; // G1: allineato al CHECK constraint del DB
  publicCode?: string; // Shortlink code (DB: public_code) — usato in /res/:code al posto di id+token
  lang?: string;
  booking_number?: string; // User-friendly ID (e.g., "DSK-1004")
  notification_preference?: 'whatsapp' | 'email' | 'both'; // Customer channel preference
  cancelled_by?: 'customer' | 'restaurant'; // Who cancelled the booking
  isArrived?: boolean; // Customer has arrived (DB: is_arrived)
  rejectionReason?: string | null; // G/J: motivazione rifiuto owner. null = cancellazione generica.
  isLargeGroup?: boolean; // G/I: true se flusso comitiva (party_size > maxPartySize)
  cancelledSilently?: boolean; // Skip notifications on cancellation (DB: cancelled_silently)
  skipRestaurantNotification?: boolean; // Skip restaurant notifications on manual booking creation
  source?: 'online' | 'manual'; // Origine: online (widget pubblico) o manual (staff). DB: source. Opzionale per non rompere i costruttori parziali di Booking (mock/test/stati form)
  marketingConsent?: boolean; // Consenso marketing del cliente (DB: bookings.marketing_consent). Default false.
}

// Contatto cliente aggregato da customer_contacts (tab "Contatti" in Prenotazioni).
// Email e telefono sono le uniche chiavi identitarie usate dalla deduplica lato DB;
// fullName è un'etichetta modificabile che non partecipa mai al matching.
export interface CustomerContact {
  id: string;
  restaurantId: string;
  fullName: string;
  email?: string | null;
  phoneE164?: string | null;
  phoneRaw?: string | null;
  bookingsCount: number; // Tutte le prenotazioni ricevute nel tempo, incluse cancelled/no_show
  firstBookingAt: string; // ISO
  lastBookingAt?: string | null; // ISO — data del servizio, può essere futura
  notes?: string | null;
  marketingConsent: boolean;
  marketingConsentAt?: string | null; // ISO — quando il consenso è stato raccolto/riconfermato (prova, §4.2)
  mergeCandidateId?: string | null; // Possibile duplicato da unire manualmente (v1.1)
  source: 'booking' | 'manual' | 'import';
  createdAt: string;
  updatedAt: string;
}

export interface MarketingStrategy {
  strengths: string[];
  weaknesses: string[];
  strategy: { title: string; description: string }[];
  socialPostIdeas: { title: string; content: string }[];
  reviewKeywords?: { text: string; sentiment: 'positive' | 'neutral' | 'negative'; frequency: number }[];
  menuSuggestions?: { name: string; reason: string }[];
}

export interface OrderItem {
  product_id: string | number;
  qty: number;
}

export interface Order {
  id: string;
  creation_date?: string;
  d?: string;
  date?: string;
  status: string;
  status_label?: string;
  total: string | number;
  items?: OrderItem[];
  payment_method?: string;
}

// Stato abbonamento restituito dalla RPC get_restaurant_billing_status
// Accessibile sia all'owner che ai membri (SECURITY DEFINER).
// NON contiene dati sensibili (niente Stripe ID, importi, ecc.)
export interface SubscriptionStatus {
  is_active: boolean;
  plan_name: string | null;
  status: 'active' | 'cancelled' | 'grace_period' | 'expired' | 'none' | 'onboarding';
  expires_at: string | null; // ISO timestamp
  grace_period: boolean;
}

export interface RestaurantData {
  directoryId?: string | number;
  slug?: string;
  publicUuid?: string;
  shortCode?: string;
  parentCategoryId?: string | number;
  parentCategoryName?: string;
  isMenuLoading?: boolean;
  isMenuInitialized?: boolean;
  menuStatus?: 'idle' | 'loading' | 'ready' | 'error';
  promotedDish?: PromotedDish; // Dati del piatto in promozione
  extraFieldIds?: {
    parentCategoryId?: string | number;
    whatsappPhone?: string | number;
    hideBookingWhenOpen?: string | number;
    hideWhatsappWhenOpen?: string | number;
    holidayClosures?: string | number;
    marketingStrategy?: string | number;
    themeColors?: string | number;
    layoutType?: string | number;
  };
  user: User;
  businessInfo: BusinessInfo;
  profileInfo: ProfileInfo;
  billingInfo: BillingInfo;
  menu: MenuCategory[];
  allergens: Allergen[];
  enabledAllergenIds?: string[];
  bookingSettings: BookingSettings;
  bookings: Booking[];
  rooms: Room[];
  marketingStrategy?: MarketingStrategy;
  customerPreferences?: CustomerPreferences;
  ownerId?: string; // Add ownerId to track and exclude owner from analytics
  currentUserRole?: 'owner' | 'member'; // [NEW] Role of the current user
  permissions?: Permissions; // [NEW] Access permissions
  onboarding_status?: 'completed' | 'in_progress' | 'finalizing'; // Status help for redirection
  // Subscription status — disponibile per OWNER e MEMBER tramite RPC SECURITY DEFINER
  // I membri vedono solo is_active/plan_name/status, SENZA dati sensibili di fatturazione
  subscriptionStatus?: SubscriptionStatus;
  // SEO Fields
  seo_title?: string;
  seo_description?: string;
  seo_language?: string;
  seo_indexable?: boolean;
  pdfSettings?: PrintSettings;
  menuCurrency?: string; // ISO 4217 — e.g. 'EUR', 'GBP'. Default 'EUR' se assente.
  ai_assistant_enabled?: boolean; // Toggle AI Assistant per ristorante (per test/beta mode)
}

export interface Permissions {
  can_manage_menu?: boolean;
  can_manage_orders?: boolean;
  can_manage_bookings?: boolean;
  can_view_stats?: boolean;
  can_manage_marketing?: boolean;
  can_manage_appearance?: boolean;      // Aspetto e Brand
  can_manage_team?: boolean;
  can_manage_subscription?: boolean;    // Abbonamento
  can_manage_settings?: boolean;        // Profilo / Impostazioni
  can_use_ai_assistant?: boolean;       // AI Assistant
  can_manage_google_business?: boolean; // Google Business Profile
  notify_bookings?: boolean;            // Ricevi push per nuove prenotazioni
  [key: string]: boolean | undefined;
}

export interface PrintSettings {
  layout: 'single' | 'double';
  font: string; // font-family value
  typography: {
    titleSize: number;
    categorySize: number;
    dishNameSize: number;
    descriptionSize: number;
    priceSize: number;
    lineHeight: number;
  };
  colors: {
    background: string;
    title: string; // Restaurant Name
    category: string;
    dishName: string;
    description: string;
    price: string;
  };
  showLogo: boolean;
  showDeskbayBranding: boolean;
}
